/* eslint-disable max-lines-per-function */
import { ReactComponent as DoneIcon } from '@paradigm/icons/src/mailru/status/done.svg';
import classNames from 'clsx';
import { ReactComponent as InfoIcon } from 'img/icons/uploader/info.svg';
import { ReactComponent as WarningIcon } from 'img/icons/uploader/warning.svg';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { publishHelper } from 'reactApp/appHelpers/publishHelper';
import { historyPush } from 'reactApp/modules/router/router.module';
import { scrollToHomeItemAction } from 'reactApp/modules/selections/selections.actions';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { cancelDescriptor } from 'reactApp/modules/uploading/uploading.module';
import {
    getFolderCloudPath,
    hasProblemFile,
    isFileDone,
    isFolder,
    showErrorIcon,
    showInfoIcon,
} from 'reactApp/modules/uploadList/uploadList.getters';
import { sendGaUploader } from 'reactApp/modules/uploadList/uploadList.helpers';
import { EFileError, EFileStatus, IInputFile } from 'reactApp/modules/uploadList/uploadList.model';
import { DOCUMENT_ID, ITEMS_META } from 'reactApp/sections/PersonalDocuments/PersonalDocuments.constants';
import FilePicture from 'reactApp/ui/FilePicture/FilePicture';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { LoaderIcon } from 'reactApp/ui/UploadList/Item/LoaderIcon';
import { LinkIcon } from 'reactApp/ui/VKUIIcons';
import { getPublicParentUrl } from 'reactApp/utils/urlHelper';

import styles from './Item.css';

interface IProps {
    file: IInputFile;
    show: boolean;
    updateUploadFiles: (data) => void;
    showError: (file: IInputFile) => void;
    isMobile?: boolean;
    storage: EStorageType;
}

export const Item = memo(({ file, show, updateUploadFiles, showError, isMobile = false, storage }: IProps) => {
    const [iconHover, setIconHover] = useState(false);
    const [fileTitle, setFileTitle] = useState('');
    const itemRef = useRef<HTMLDivElement>(null);
    const publishRef = useRef<HTMLDivElement>(null);
    const fileDone = isFileDone(file);
    const fileProblems = hasProblemFile(file);
    const isInfoIcon = showInfoIcon(file);
    const isErrorIcon = showErrorIcon(file);
    const dispatch = useDispatch();
    const { isReactPage, isPublic, isHome, isAlbums } = getStorage(storage);
    const iconName = file?.extension?.toLowerCase();

    useEffect(() => {
        if (show) {
            itemRef?.current?.scrollIntoView();
        }
    }, [show]);

    useEffect(() => {
        if (fileDone) {
            const itemType = isFolder(file) ? 'папке' : 'файлу';
            setFileTitle(`Перейти к ${itemType}`);
        }
    }, [fileDone]);

    const publishHandler = useCallback(
        (event) => {
            event.stopPropagation();

            sendGaUploader('file', 'publish');
            let id = file.cloudPath;

            if (isFolder(file)) {
                id = getFolderCloudPath(file);
            }

            if (id) {
                publishHelper({ item: { id, storage: isAlbums ? EStorageType.home : undefined }, publishFrom: 'uploader' });
                updateUploadFiles({ descriptorId: file.descriptorId, cloudPath: file.cloudPath, publish: true });
            }
        },
        [file]
    );

    const cancelHandler = useCallback(() => {
        if (fileProblems) {
            return;
        }

        if (file?.files?.length || file?.extension === 'folder') {
            dispatch(cancelDescriptor(file.localPath));
        }

        updateUploadFiles({
            cloudPath: file.cloudPath,
            descriptorId: file.descriptorId,
            status: EFileStatus.CANCEL,
            error: EFileError.CANCELLED_FILE,
            hideError: true,
        });
    }, [file]);

    const onCloseMouseEnter = useCallback(() => {
        setIconHover(true);
    }, []);

    const onCloseMouseLeave = useCallback(() => {
        setIconHover(false);
    }, []);

    const showMessageHandler = useCallback(
        (e) => {
            e.stopPropagation();

            showError(file);
        },
        [file, showError]
    );

    const showItemHandler = useCallback(
        (event) => {
            const publishElem = publishRef?.current;
            const isPublishClick = publishElem === event?.target || event?.target?.parentElement === publishElem;

            if (!fileDone || isPublishClick) {
                if (isMobile && (isInfoIcon || isErrorIcon)) {
                    showError(file);
                }
                return;
            }

            if (file.documentType) {
                const path = ITEMS_META[DOCUMENT_ID[file.documentType]]?.title;
                if (!path) {
                    return;
                }

                dispatch(historyPush({ id: `/documents/${path}` }));
                return;
            }

            const isItemFolder = isFolder(file);
            let id = file.cloudPath;

            if (isItemFolder) {
                id = getFolderCloudPath(file);

                if (isPublic) {
                    id = `${getPublicParentUrl()}/${file.cloudPath}`;
                }
            }

            dispatch(scrollToHomeItemAction({ itemId: id, isFolder: isItemFolder }));
        },
        [file, showError]
    );

    const getLoaderIcon = () => {
        if (fileProblems) {
            return null;
        }

        return <LoaderIcon progress={file.progress} loading={file.currentUpload} iconHover={iconHover} />;
    };

    const getMessageIcon = () => {
        let icon;
        let text;

        if (isErrorIcon) {
            text = 'Показать ошибку';
            icon = (
                <div className={classNames({ [styles.message]: true, [styles.warning]: true })} onClick={showMessageHandler}>
                    <WarningIcon />
                </div>
            );
        } else if (isInfoIcon && (!isMobile || !fileDone)) {
            text = 'Показать сообщение';
            icon = (
                <div className={classNames({ [styles.message]: true, [styles.info]: true })} onClick={showMessageHandler}>
                    <InfoIcon />
                </div>
            );
        }

        if (!icon) {
            return null;
        }

        return <Hint text={text}>{icon}</Hint>;
    };

    const getPublishIcon = () => {
        if (!fileDone || isPublic) {
            return null;
        }

        const publish = file.publish ? (
            <Hint text="Открыть доступ к файлу">
                <LinkIcon />
            </Hint>
        ) : (
            <>
                <LinkIcon />
                <span className={styles.publishText}>Поделиться</span>
            </>
        );

        return (
            <div
                className={classNames({ [styles.publish]: true, [styles.small]: file.publish })}
                onClick={publishHandler}
                ref={publishRef}
                title=""
            >
                {publish}
            </div>
        );
    };

    return (
        <div
            className={classNames({
                [styles.root]: true,
                [styles.cursor]: fileDone && (!isReactPage || isHome),
                [styles.highlight]: file.highlight,
                [styles.root_mobile]: isMobile,
            })}
            ref={itemRef}
            onClick={showItemHandler}
            data-qa-filename={file.extension ? `${file.name}.${file.extension}` : file.name}
            title={fileTitle}
        >
            <div className={styles.elements}>
                <div className={classNames({ [styles.wrapperText]: true, [styles.wrapperText_progress]: !fileDone })}>
                    <div className={styles.type}>
                        <FilePicture
                            icon={iconName}
                            file={file}
                            thumbUrl={isFolder(file) ? '' : file.thumb}
                            borderRadius={true}
                            removeLoader={() => true}
                            loadThumb={() => true}
                        />
                    </div>
                    <div className={styles.name}>
                        <NameComponent name={file.name} extension={!isFolder(file) ? file?.extension : undefined} truncate />
                    </div>
                    {getMessageIcon()}
                </div>
                {getPublishIcon()}
                {!fileDone && (
                    <div
                        className={styles.LoadingIconWrap}
                        onClick={cancelHandler}
                        onMouseEnter={onCloseMouseEnter}
                        onMouseLeave={onCloseMouseLeave}
                    >
                        {getLoaderIcon()}
                    </div>
                )}
                {fileDone && isMobile && (
                    <div className={styles.icon}>
                        <DoneIcon width={16} height={16} />
                    </div>
                )}
            </div>
        </div>
    );
});

Item.displayName = 'UploadListItem';
